import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "./rules.css";
import "../themeupdate/themeupdate.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import { normalizeThemeHexColor } from "../../../functions/themeAssets";
import RulesFeatureTour, {
  markRulesTourSeen,
  shouldAutoStartRulesTour,
} from "./RulesFeatureTour";

const MAX_RULES = 6;
const MIN_RULES = 1;
const MAX_RULE_CHARS = 200;
const MAX_TITLE_CHARS = 150;

const COLOR_FIELDS = [
  { key: "landing_page_title_color", label: "Text Color", fallback: "#000000" },
  { key: "sub_color", label: "Sub text color", fallback: "#ffffff" },
  { key: "button_color", label: "Button Color", fallback: "#e9695e" },
  { key: "button_text_color", label: "Button Text Color", fallback: "#ffffff" },
];

function parseStringList(raw) {
  if (Array.isArray(raw)) return raw.map((r) => String(r ?? ""));
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.map((r) => String(r ?? ""));
    } catch {
      /* not JSON */
    }
  }
  return [];
}

const Rules = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState("");
  const [rules, setRules] = useState([""]);
  const [colors, setColors] = useState(() =>
    Object.fromEntries(COLOR_FIELDS.map((f) => [f.key, f.fallback]))
  );
  const [tourOpen, setTourOpen] = useState(false);

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (status === "loading" && !data) return undefined;
    if (!shouldAutoStartRulesTour()) return undefined;
    const t = window.setTimeout(() => setTourOpen(true), 600);
    return () => window.clearTimeout(t);
  }, [status, data]);

  const closeTour = () => {
    markRulesTourSeen();
    setTourOpen(false);
  };

  useEffect(() => {
    if (!data) return;
    setTitle(String(data.landing_page_title ?? ""));
    const loadedRules = parseStringList(data.rules);
    setRules(loadedRules.length ? loadedRules : [""]);
    setColors(
      Object.fromEntries(
        COLOR_FIELDS.map((f) => [f.key, normalizeThemeHexColor(data[f.key]) || f.fallback])
      )
    );
  }, [data]);

  const updateRule = (index, value) => {
    setRules((prev) => prev.map((rule, i) => (i === index ? value : rule)));
  };

  const addRule = () => {
    if (rules.length >= MAX_RULES) return;
    setRules((prev) => [...prev, ""]);
  };

  const removeRule = (index) => {
    if (rules.length <= MIN_RULES) return;
    setRules((prev) => prev.filter((_, i) => i !== index));
  };

  // Same checks, order and saved fields as old/admin/rules.php -> events.php?events=add_rules.
  const validate = () => {
    const filled = rules.map((r) => String(r ?? "").trim()).filter(Boolean);
    if (!filled.length) return { ok: false, msg: "At least 1 rule required." };
    if (!title.trim()) {
      return { ok: false, msg: "The title cannot be empty or contain only spaces." };
    }
    if (title.length > MAX_TITLE_CHARS) {
      return { ok: false, msg: "You exceed the maximum limit of the title." };
    }
    if (rules.some((r) => String(r ?? "").length > MAX_RULE_CHARS)) {
      return { ok: false, msg: `Rules must below ${MAX_RULE_CHARS} characters.` };
    }
    return {
      ok: true,
      payload: { rules: filled, landing_page_title: title, ...colors },
    };
  };

  const handleNext = async () => {
    const v = validate();
    if (!v.ok) {
      Swal.fire("Error", v.msg, "error");
      return;
    }
    if (!adminToken || !currentTheme) {
      Swal.fire("Error", "Admin session or theme is missing.", "error");
      return;
    }

    setLoading(true);
    try {
      await updateThemeData({
        payload: {
          data: v.payload,
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      await Swal.fire({ title: "Data Updated", icon: "success", timer: 1000, showConfirmButton: false });
      navigate("/admin/themeupdate");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save", "error");
    } finally {
      setLoading(false);
    }
  };

  if (status === "loading" && !data) {
    return (
      <div className="rules-page-new">
        <div className="themeupdate-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="rules-shell">
      <div className="back-button-holder rules-top-bar admin-tour-top-bar">
        <button type="button" className="back-button" onClick={() => navigate("/admin")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
        <button
          type="button"
          className="admin-tour-launch"
          onClick={() => setTourOpen(true)}
          aria-label="Start feature tour"
        >
          <i className="fa-solid fa-route" aria-hidden />
          Take a tour
        </button>
      </div>

      <div className="rules-main-grid">
        <section className="rules-card" data-tour="tour-theme-settings">
          <div className="rules-card-body">
            <div className="rules-field-group" data-tour="tour-custom-title">
              <label className="rules-field-label" htmlFor="rules-title">
                Add Custom Title:
              </label>
              <textarea
                id="rules-title"
                className="rules-title-input"
                placeholder="Enter the title shown on the rules page"
                maxLength={MAX_TITLE_CHARS}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
              <p className="rules-char-count">
                {title.length}/{MAX_TITLE_CHARS}
              </p>
              <p className="rules-field-hint">
                <code>Maximum character limit should be {MAX_TITLE_CHARS} characters.</code>
                <code>This title will reflect on the main game page</code>
              </p>
            </div>

            <div className="rules-field-group" data-tour="tour-custom-rules">
              <label className="rules-field-label">Add Custom Rules</label>
              <div className="rules-repeater">
                {rules.map((rule, index) => {
                  const charCount = String(rule ?? "").length;
                  return (
                    <div className="rules-repeater-row" key={`rule-${index}`}>
                      <span className="rules-repeater-index">{index + 1}</span>
                      <div className="rules-repeater-field">
                        <textarea
                          name="rules"
                          className="rules-repeater-textarea"
                          placeholder="Rules"
                          value={rule}
                          onChange={(e) => updateRule(index, e.target.value)}
                        />
                        <p
                          className={`rules-char-count${charCount > MAX_RULE_CHARS ? " is-over" : ""}`}
                        >
                          {charCount}/{MAX_RULE_CHARS}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="rules-remove-btn"
                        onClick={() => removeRule(index)}
                        disabled={rules.length <= MIN_RULES || loading}
                        aria-label="Remove rule"
                        title="Remove rule"
                      >
                        <i className="fa-solid fa-xmark" aria-hidden />
                      </button>
                    </div>
                  );
                })}
              </div>
              <button
                type="button"
                className="rules-add-btn"
                onClick={addRule}
                disabled={rules.length >= MAX_RULES || loading}
              >
                <i className="fa-solid fa-plus" aria-hidden /> Add new rule
              </button>
              <p className="rules-field-hint">
                <code>Keep {MAX_RULES} rules at the most</code>
                <code>At least {MIN_RULES} rule is required</code>
                <code>Character limit of each rule is {MAX_RULE_CHARS}</code>
              </p>
            </div>
          </div>
        </section>

        <div className="rules-stack" data-tour="tour-page-content">
          <section className="rules-card">
            <header className="rules-card-header">
              <span className="rules-card-icon">
                <i className="fa-solid fa-palette" aria-hidden />
              </span>
              <div>
                <h2 className="rules-card-title">Page Content</h2>
              </div>
            </header>

            <div className="rules-card-body">
              <div className="rules-color-grid">
                {COLOR_FIELDS.map((f) => (
                  <label className="rules-color-chip" key={f.key}>
                    <span className="rules-color-chip-swatch" style={{ background: colors[f.key] }}>
                      <input
                        type="color"
                        value={colors[f.key]}
                        onChange={(e) =>
                          setColors((prev) => ({ ...prev, [f.key]: e.target.value }))
                        }
                      />
                    </span>
                    <span className="rules-color-chip-label">{f.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </section>
        </div>
      </div>

      <div className="rules-sticky-actions" data-tour="tour-actions">
        <button
          type="button"
          className="save-and-continue"
          onClick={handleNext}
          disabled={loading}
        >
          {loading ? "Saving..." : "Next"} <i className="fa-solid fa-angle-double-right" aria-hidden />
        </button>
      </div>

      <RulesFeatureTour open={tourOpen} onClose={closeTour} />
    </div>
  );
};

export default Rules;
